import { useEffect, useState } from 'react';
import type { PairingCode } from '../../api/types.js';
import { secondsUntil } from '../../core/format.js';

/**
 * Port of Android's `PairingCodeCard`.
 *
 * Shows a freshly generated pairing code, plaintext, exactly as the
 * backend returns it — this is the only time it is ever visible. It is
 * redeemed on whatever device is meant to become the child device, in
 * that device's own app; there is deliberately no "redeem here" shortcut,
 * since a web browser is never a child device.
 *
 * "Abbrechen" is offered the whole time this card is visible, expired or
 * not — there is no push mechanism to notice a redeem or an expiry, so
 * without it a stale code would just sit here until the next manual
 * refresh.
 */
export function PairingCodeCard({
  pairingCode,
  cancelling,
  onCancel,
}: {
  pairingCode: PairingCode;
  cancelling: boolean;
  onCancel: () => void;
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(() => secondsUntil(pairingCode.expiresAt));

  useEffect(() => {
    setRemainingSeconds(secondsUntil(pairingCode.expiresAt));
    const interval = setInterval(() => {
      setRemainingSeconds(secondsUntil(pairingCode.expiresAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [pairingCode.expiresAt]);

  return (
    <div className="card card--inset pairing">
      <div>
        {/* aria-label spells the code out so a screen reader doesn't run
            the characters together into an unintelligible word. */}
        <p className="pairing__code" aria-label={pairingCode.code.split('').join(' ')}>
          {pairingCode.code}
        </p>
        <p className="muted" aria-live="polite">
          {remainingSeconds > 0 ? `Läuft ab in ${remainingSeconds}s` : 'Abgelaufen'}
        </p>
      </div>
      <button type="button" className="button button--text" onClick={onCancel} disabled={cancelling}>
        Abbrechen
      </button>
    </div>
  );
}
